import { useCallback, useEffect, useRef, useState } from "react";

import { ApplicationEndpoints } from "@generated/application";
import { useApi } from "@platform/hooks/useApi";
import { useSse, type SseMessage } from "@platform/hooks/useSse";

import { Application } from "../types";

interface UseStacksByProjectReturn {
  systems: Application[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Fetches the list of systems (applications) for a given project alias.
 * Also subscribes to SSE `application_created` events to auto-refresh
 * when a new stack is created.
 */
export function useStacksByProject(
  projectAlias: string | undefined,
): UseStacksByProjectReturn {
  const api = useApi();
  const [systems, setSystems] = useState<Application[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!projectAlias) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.get<Application[]>(
        `${ApplicationEndpoints.LIST_APPLICATIONS}?projectAlias=${encodeURIComponent(projectAlias)}`,
        {
          errorMessage: `Failed to load systems for project: ${projectAlias}`,
          showLoading: false,
        },
      );
      setSystems(Array.isArray(result) ? result : []);
    } catch {
      setError("Failed to load stacks.");
    } finally {
      setLoading(false);
    }
  }, [projectAlias]);

  // Keep latest load fn in a ref so the SSE callback doesn't change identity
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  // Initial load
  useEffect(() => {
    void load();
  }, [load]);

  // SSE subscription for real-time stack creation
  const ssePath = projectAlias ? `sse/project/${projectAlias}` : null;

  const handleMessage = useCallback((msg: SseMessage) => {
    if (msg.type === "application_created") {
      void loadRef.current();
    }
  }, []);

  useSse(ssePath, handleMessage);

  return { systems, loading, error, reload: load };
}
